import { useState, useEffect } from 'react';
import { getStaff, createStaff, updateStaff, deleteStaff } from '../utils/api';
import api from '../utils/api';
import toast from 'react-hot-toast';

const ROLES = ['Sales Rep', 'Telemarketer', 'Canvasser', 'Confirmer'];
const empty = { name: '', role: 'Sales Rep', status: 'Active' };

const fmt = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt0 = n => '$' + (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtK = n => {
  const v = Number(n) || 0;
  if (v >= 1000) return '$' + Math.round(v / 1000) + 'K';
  return '$' + Math.round(v);
};

// Per-role colors + icon (emoji kept simple; matches the colored-badge ask)
const ROLE_STYLE = {
  'Sales Rep':     { bg: '#E6F1FB', fg: '#0C447C', icon: '💼' },
  'Telemarketer':  { bg: '#EEEDFE', fg: '#3C3489', icon: '📞' },
  'Canvasser':     { bg: '#e1e9f5', fg: '#0e336f', icon: '🚶' },
  'Confirmer':     { bg: '#FBEAF0', fg: '#72243E', icon: '🎧' },
};
const roleStyle = r => ROLE_STYLE[r] || { bg: '#F1EFE8', fg: '#444441', icon: '👤' };
const initials = name => (name || '').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

export default function Staff() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showModal, setShowModal] = useState(false);

  // Staff detail view
  const [detailFor, setDetailFor] = useState(null);   // staff member being viewed
  const [detail, setDetail] = useState(null);          // fetched detail payload
  const [override, setOverride] = useState(null);      // manager override (if any)
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('summary');
  const [summary, setSummary] = useState({}); // name(lower) -> {total_jobs,total_sales,active,closed}

  useEffect(() => { getStaff().then(setList); }, []);

  useEffect(() => {
    api.get('/canvasser-commission/staff-summary')
      .then(res => {
        const map = {};
        (Array.isArray(res.data) ? res.data : []).forEach(s => { map[(s.name || '').toLowerCase()] = s; });
        setSummary(map);
      })
      .catch(() => {});
  }, []);

  async function openDetail(s) {
    setDetailFor(s); setDetail(null); setOverride(null); setDetailTab('summary'); setDetailLoading(true);
    try {
      const res = await api.get(`/canvasser-commission/staff-detail/${encodeURIComponent(s.name)}`);
      setDetail(res.data);
      // Managers with a team override
      if (['alfredo','jp','carlos'].includes((s.name || '').toLowerCase().trim())) {
        try {
          const o = await api.get(`/canvasser-commission/team-override/${encodeURIComponent(s.name)}`);
          setOverride(o.data);
        } catch {}
      }
    } catch (e) {
      toast.error('Failed to load staff detail');
    } finally {
      setDetailLoading(false);
    }
  }
  function closeDetail() { setDetailFor(null); setDetail(null); setOverride(null); }

  function openAdd() { setEditing(null); setForm(empty); setShowModal(true); }
  function openEdit(s) { setEditing(s); setForm({ ...s }); setShowModal(true); }

  async function handleSave() {
    if (!form.name?.trim()) return toast.error('Name required');
    try {
      if (editing) {
        const updated = await updateStaff(editing.id, form);
        setList(l => l.map(s => s.id === editing.id ? updated : s));
        toast.success('Staff updated');
      } else {
        const created = await createStaff(form);
        setList(l => [...l, created]);
        toast.success('Staff added');
      }
      setShowModal(false);
    } catch {
      toast.error('Error saving');
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete ${name}?`)) return;
    await deleteStaff(id);
    setList(l => l.filter(s => s.id !== id));
    toast.success('Deleted');
  }

  const byRole = ROLES.reduce((acc, r) => ({ ...acc, [r]: list.filter(s => s.role === r) }), {});

  return (
    <>
      <div className="page-header">
        <h2>Staff</h2>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Staff</button>
      </div>

      <div className="page-body">
        {ROLES.map(role => byRole[role].length === 0 ? null : (
          <div key={role} className="card" style={{ marginBottom: 20, overflow: 'hidden' }}>
            <div className="card-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden="true">{roleStyle(role).icon}</span> {role}s
              </h3>
              <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{byRole[role].length} members</span>
            </div>
            <div>
              {byRole[role].map((s, idx) => {
                const st = roleStyle(s.role);
                const sum = summary[(s.name || '').toLowerCase()] || {};
                const inactive = s.status !== 'Active';
                return (
                  <div key={s.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                      borderTop: idx === 0 ? 'none' : '1px solid var(--gray-100, #f0f0f0)', opacity: inactive ? 0.55 : 1 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: st.bg, color: st.fg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                      {initials(s.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: st.bg, color: st.fg,
                        fontSize: 11, padding: '1px 8px', borderRadius: 10, marginTop: 2 }}>
                        <span aria-hidden="true">{st.icon}</span> {s.role}{inactive ? ' · Inactive' : ''}
                      </span>
                    </div>
                    <Stat label="Jobs" value={sum.total_jobs ?? '—'} />
                    <Stat label="Sales" value={sum.total_sales != null ? fmtK(sum.total_sales) : '—'} />
                    <Stat label="Active" value={sum.active ?? '—'} color="#1a355f" />
                    <Stat label="Closed" value={sum.closed ?? '—'} color="#555" />
                    <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--green-600)', fontWeight: 600 }} onClick={() => openDetail(s)}>View</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red-500)' }} onClick={() => handleDelete(s.id, s.name)}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Staff' : 'Add Staff'}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group form-full">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={form.name || ''} onChange={e => setForm(x => ({ ...x, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={form.role} onChange={e => setForm(x => ({ ...x, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e => setForm(x => ({ ...x, status: e.target.value }))}>
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
      {detailFor && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 880, width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>{detailFor.name} <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)', fontWeight: 400 }}>· {detailFor.role}</span></h3>
              <button className="btn btn-ghost btn-sm" onClick={closeDetail}>✕</button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto' }}>
              {detailLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>Loading…</div>
              ) : !detail ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>No data</div>
              ) : (
                <>
                  {/* Tabs */}
                  <div style={{ display: 'flex', gap: 0, marginBottom: 18, borderBottom: '2px solid #eee' }}>
                    {[['summary','Summary'],['jobs',`Jobs (${detail.jobs.length})`],['commission','Commission']].map(([key,label]) => (
                      <button key={key} onClick={() => setDetailTab(key)}
                        style={{ padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                          fontWeight: detailTab===key?700:400, color: detailTab===key?'#0d1f3b':'#888',
                          borderBottom: detailTab===key?'2px solid #0d1f3b':'2px solid transparent', marginBottom: -2 }}>{label}</button>
                    ))}
                  </div>

                  {/* SUMMARY */}
                  {detailTab === 'summary' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
                      <DCard label="Total Jobs" value={detail.summary.total_jobs} color="#0d1f3b" />
                      <DCard label="Active" value={detail.summary.active_jobs} color="#1a355f" />
                      <DCard label="Completed" value={detail.summary.completed_jobs} color="#1565C0" />
                      <DCard label="Cancelled" value={detail.summary.cancelled_jobs} color="#c62828" />
                      <DCard label="Total Sales" value={fmt0(detail.summary.total_sales)} color="#F57F17" sub="Their share (after split)" />
                      <DCard label="Avg Job Size" value={fmt0(detail.summary.avg_job_size)} color="#6a1a9a" />
                      {!detail.is_sales_rep && <DCard label="Paid Commission" value={fmt(detail.summary.total_paid)} color="#1a355f" />}
                      {!detail.is_sales_rep && <DCard label="Pending Commission" value={fmt(detail.summary.total_pending)} color="#F57F17" />}
                    </div>
                  )}

                  {/* JOBS */}
                  {detailTab === 'jobs' && (
                    detail.jobs.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No jobs found</div> : (
                    <div className="table-wrap">
                      <table style={{ fontSize: 13, width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#0d1f3b', color: '#fff' }}>
                            <th style={jh}>PO #</th>
                            <th style={jh}>Job #</th>
                            <th style={jh}>Customer</th>
                            <th style={jh}>Role</th>
                            <th style={{ ...jh, textAlign: 'right' }}>Job Value</th>
                            <th style={{ ...jh, textAlign: 'right' }}>Split</th>
                            <th style={jh}>Sales Status</th>
                            <th style={jh}>Prod Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.jobs.map((j, i) => {
                            const rank = j.job_rank || 1;
                            const ordinal = rank === 1 ? '1st' : rank === 2 ? '2nd' : rank === 3 ? '3rd' : `${rank}th`;
                            const eligible = j.role_on_job === 'Sales Rep' ? true : (rank <= 2);
                            return (
                            <tr key={j.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 ? '#fafafa' : '#fff' }}>
                              <td style={jd}>{j.po_number}</td>
                              <td style={jd}>
                                <span title={eligible ? 'Commission-eligible' : 'Not eligible (past first two)'}
                                  style={{ fontSize: 11, fontWeight: 600, color: eligible ? '#1a355f' : '#c62828' }}>
                                  {ordinal}{eligible ? '' : ' ✕'}
                                </span>
                              </td>
                              <td style={{ ...jd, fontWeight: 600 }}>{j.customer_name}</td>
                              <td style={jd}>{j.role_on_job}</td>
                              <td style={{ ...jd, textAlign: 'right' }}>{fmt0(j.job_value)}</td>
                              <td style={{ ...jd, textAlign: 'right' }}>{j.split_pct}%</td>
                              <td style={jd}><span className={statusClass(j.sales_status)}>{j.sales_status || '—'}</span></td>
                              <td style={{ ...jd, fontSize: 12, color: '#666' }}>{j.prod_status || '—'}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
                        “Job #” is the order for that customer. Canvassers/telemarketers earn on the 1st & 2nd; sales reps on all; ✕ = not commission-eligible.
                      </div>
                    </div>
                    )
                  )}

                  {/* COMMISSION */}
                  {detailTab === 'commission' && (
                    detail.is_sales_rep ? (
                      <div style={{ padding: 20, background: '#fff8e1', borderRadius: 8, color: '#8a6d00', fontSize: 14 }}>
                        💡 Sales Rep commission isn't configured yet. This tool shows jobs and sales totals for sales reps; a sales-rep commission module is a planned feature.
                      </div>
                    ) : (
                    <>
                      {/* Manager override */}
                      {override && (
                        <div style={{ background: '#fff', border: '1px solid #e3f2fd', borderRadius: 10, marginBottom: 18, overflow: 'hidden' }}>
                          <div style={{ padding: '10px 16px', background: '#1565C0', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                            👑 Manager Override — {(override.overrideRate * 100).toFixed(1)}% on Team Sales
                          </div>
                          <div style={{ padding: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <MiniStat label="Total Override" value={fmt(override.totalOverride)} color="#1565C0" />
                            <MiniStat label="Paid" value={fmt(override.paidOverride)} color="#1a355f" />
                            <MiniStat label="Pending" value={fmt(override.pendingOverride)} color="#F57F17" />
                          </div>
                        </div>
                      )}

                      {/* Pending */}
                      <SectionHead color="#F57F17">⏳ Pending Commission ({detail.pending_jobs.length})</SectionHead>
                      {detail.pending_jobs.length === 0 ? (
                        <div style={{ padding: 16, color: '#aaa', fontSize: 13 }}>Nothing pending.</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 18 }}>
                          <thead><tr style={{ background:'#fff8e1' }}>
                            <th style={th}>Customer</th><th style={th}>PO#</th><th style={thR}>Effective Value</th><th style={thR}>Est. Commission</th>
                          </tr></thead>
                          <tbody>
                            {detail.pending_jobs.map(j => (
                              <tr key={j.id} style={{ borderBottom:'1px solid #f5f5f5' }}>
                                <td style={td}>{j.customer_name}</td>
                                <td style={{ ...td, color:'#888' }}>{j.po_number}</td>
                                <td style={tdR}>{fmt(j.effective_value)}</td>
                                <td style={{ ...tdR, fontWeight:700, color:'#F57F17' }}>{fmt(j.commission_est)}</td>
                              </tr>
                            ))}
                            <tr style={{ background:'#fff8e1' }}>
                              <td style={{ ...td, fontWeight:700 }} colSpan={3}>Total Pending</td>
                              <td style={{ ...tdR, fontWeight:700, color:'#F57F17' }}>{fmt(detail.summary.total_pending)}</td>
                            </tr>
                          </tbody>
                        </table>
                      )}

                      {/* Paid history */}
                      <SectionHead color="#1a355f">✅ Paid Commission ({detail.paid_entries.length})</SectionHead>
                      {detail.paid_entries.length === 0 ? (
                        <div style={{ padding: 16, color: '#aaa', fontSize: 13 }}>No paychecks processed yet.</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead><tr style={{ background:'#f9f9f9' }}>
                            <th style={th}>Pay Date</th><th style={th}>Customer</th><th style={th}>PO#</th><th style={thR}>Commission</th>
                          </tr></thead>
                          <tbody>
                            {detail.paid_entries.map(p => (
                              <tr key={p.id} style={{ borderBottom:'1px solid #f5f5f5' }}>
                                <td style={td}>{p.pay_date}</td>
                                <td style={td}>{p.customer_name || '—'}</td>
                                <td style={{ ...td, color:'#888' }}>{p.po_number || '—'}</td>
                                <td style={{ ...tdR, fontWeight:700, color:'#1a355f' }}>{fmt(p.commission_amt)}</td>
                              </tr>
                            ))}
                            <tr style={{ background:'#f1f4f8' }}>
                              <td style={{ ...td, fontWeight:700 }} colSpan={3}>Total Paid</td>
                              <td style={{ ...tdR, fontWeight:700, color:'#1a355f' }}>{fmt(detail.summary.total_paid)}</td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const th = { padding: '8px 12px', textAlign: 'left', color: '#888', fontSize: 12 };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '8px 12px' };
const tdR = { ...td, textAlign: 'right' };
const jh = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, whiteSpace: 'nowrap' };
const jd = { padding: '8px 10px' };

function statusClass(s) {
  const v = (s || '').toLowerCase();
  if (v === 'cancelled') return 'badge badge-cancelled';
  return 'badge badge-active';
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'right', minWidth: 52 }} className="staff-stat">
      <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{label}</div>
      <div style={{ fontWeight: 600, color: color || 'inherit' }}>{value}</div>
    </div>
  );
}

function DCard({ label, value, color, sub }) {  return (
    <div style={{ background: '#fff', border: `2px solid ${color}`, borderRadius: 8, padding: '10px 16px' }}>
      <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}
function MiniStat({ label, value, color }) {
  return (
    <div style={{ background: '#f7f9fc', borderRadius: 8, padding: '8px 14px', minWidth: 120 }}>
      <div style={{ fontSize: 10, color: '#777', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
function SectionHead({ children, color }) {
  return <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 8 }}>{children}</div>;
}
